import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CreditCard, Receipt, Gauge } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useBillingDialogs } from "@/contexts/BillingDialogs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BILLING_ENABLED, planLabel, isPaidPlan, usePlanLimits, formatNumber, PLANS } from "@/lib/plans";

const STATUS_LABEL = { none: "No payment method", active: "Active", past_due: "Payment failed", canceled: "Cancelled" };

/** Billing: plan, credits, today's reveal usage, payment method, invoices, cancel. */
export default function Billing() {
  const { user } = useAuth();
  const billing = useBillingDialogs();
  const navigate = useNavigate();
  const limits = usePlanLimits(user?.plan);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let active = true;
    supabase.rpc("get_my_usage").then(({ data }) => active && data && setUsage(data)).catch(() => {});
    return () => { active = false; };
  }, []);

  if (!user?.id) return null;
  const plan = user.plan || "free";
  const paid = isPaidPlan(plan);
  const left = Math.max(0, (user.credits_total ?? 0) - (user.credits_used ?? 0));
  const price = PLANS.find((p) => p.id === plan);
  const renews = user.current_period_end ? format(new Date(user.current_period_end), "d MMM yyyy") : null;
  const usedToday = usage?.used ?? 0;
  const dayLimit = usage?.limit ?? limits.daily_reveal_limit;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <div>
          <div className="eyebrow">Account</div>
          <h1 className="font-display text-2xl tracking-tight">Billing</h1>
        </div>
        <Button onClick={() => navigate("/choose-plan")} data-testid="billing-change-plan">
          {paid ? "Change plan" : "Upgrade"}
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10">
        <div className="mx-auto grid w-full max-w-3xl gap-6">
          <section className="rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="billing-plan-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="eyebrow">Current plan</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-display text-[22px]">{planLabel(plan)}</span>
                  {paid && <Badge variant="secondary">{STATUS_LABEL[user.subscription_status] || "Active"}</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {paid
                    ? `${price ? `$${price.monthly}/mo · ` : ""}${formatNumber(limits.monthly_credits)} credits a month${
                      renews ? ` · ${user.cancel_at_period_end ? "ends" : "renews"} ${renews}` : ""}`
                    : "100 free credits to try Inbound Labs. No card needed."}
                </p>
              </div>
              {paid && !user.cancel_at_period_end && (
                <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={billing.showCancel}
                  data-testid="billing-cancel-plan">
                  Cancel plan
                </Button>
              )}
            </div>
          </section>

          <section className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="billing-credits">
              <div className="eyebrow flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Credits</div>
              <div className="mt-2 font-display text-[44px] leading-none tracking-[-0.02em] tabular-nums">{formatNumber(left)}</div>
              <p className="mt-1 text-sm text-muted-foreground">left of {formatNumber(user.credits_total)}</p>
              <Button variant="outline" className="mt-4" onClick={billing.showBuyCredits} data-testid="billing-buy-credits">Buy credits</Button>
            </div>
            <div className="rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="billing-usage">
              <div className="eyebrow flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" /> Reveals today</div>
              <div className="mt-2 font-display text-[44px] leading-none tracking-[-0.02em] tabular-nums">{formatNumber(usedToday)}</div>
              <p className="mt-1 text-sm text-muted-foreground">of {formatNumber(dayLimit)} a day on your plan · resets at midnight UTC</p>
              <p className="mt-4 text-sm text-muted-foreground">
                Up to {formatNumber(limits.max_rows_per_job)} rows per Enrich upload. Search is unlimited.
              </p>
            </div>
          </section>

          <section className="rounded-xl border border-border bg-surface p-6 shadow-card">
            <div className="eyebrow flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Payment method</div>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="billing-payment-method">
              {BILLING_ENABLED
                ? (paid ? "Managed securely by Stripe." : "Added when you choose a plan.")
                : "Payments open soon. Your card will be managed securely by Stripe."}
            </p>
          </section>

          <section className="rounded-xl border border-border bg-surface p-6 shadow-card">
            <div className="eyebrow flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" /> Invoices</div>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="billing-invoices">
              No invoices yet. They appear here after your first payment.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
